import { useEffect, useRef, useState, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { toast } from '@/components/toasts/toast.helpers'
import {
  addMessage,
  updateMessage,
  setLoading,
  setRestoring,
  reset,
  renameSession,
  setReadOnly,
  setAwaitingApproval,
  setPendingInteractive,
  setActiveSessionTitle,
  setUsagePolicies,
} from '@/reducers/kodi/kodi.redux'
import {
  COWORK_WS_URL,
  CONNECTION_STATES,
  WS_MESSAGE_TYPES,
  MESSAGE_ROLES,
  TOOL_STATUSES,
  INLINE_ARTIFACT_TYPES,
} from '../kodi.constants'

const ARTIFACT_MIME_TYPES = {
  pdf: 'application/pdf',
}

const downloadArtifact = (artifactData) => {
  const mimeType = ARTIFACT_MIME_TYPES[artifactData.artifact_type] || 'application/octet-stream'
  const binaryString = atob(artifactData.data)
  const bytes = new Uint8Array(binaryString.length)

  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i)
  }

  const blob = new Blob([bytes], { type: mimeType })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')

  link.href = url
  link.download = artifactData.filename || 'download'
  link.click()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

const RECONNECT_INTERVAL_MS = 2000
const RECONNECT_JITTER_MS = 1000

export const useKodiWebSocket = (routeSessionId = null) => {
  const dispatch = useDispatch()
  const { token, username, organizationid } = useSelector((state) => state.currentUser)
  const messagesLength = useSelector((state) => state.kodi.messages.length)
  const loading = useSelector((state) => state.kodi.loading)
  const awaitingApproval = useSelector((state) => state.kodi.awaitingApproval)
  const pendingInteractive = useSelector((state) => state.kodi.pendingInteractive)
  const [connectionState, setConnectionState] = useState(CONNECTION_STATES.CONNECTING)
  const [isStreaming, setIsStreaming] = useState(false)
  const [isStopping, setIsStopping] = useState(false)
  const [streamingText, setStreamingText] = useState('')
  const [serverSessionId, setServerSessionId] = useState(null)
  // Owner org of the session + whether org 1 is viewing it with unredacted
  // access (from auth_success). Only the support-access banner on this page
  // reads these, so they live here (auto-clear on unmount) rather than in redux.
  const [ownerOrgId, setOwnerOrgId] = useState(null)
  const [isUnredactedAccess, setIsUnredactedAccess] = useState(false)
  const [accessDenied, setAccessDenied] = useState(false)
  // 4001 (the session could not be authenticated) and 4003 (authenticated but
  // not permitted) both end in `accessDenied`, but need different advice: a
  // signed-in user told "you do not have access" after an auth failure has no
  // idea that signing in again is the fix.
  const [authFailed, setAuthFailed] = useState(false)

  const wsRef = useRef(null)
  const streamingTextRef = useRef('')
  const reconnectTimeoutRef = useRef(null)
  const prevOrgRef = useRef(organizationid)
  // Ref to avoid a stale closure when connect() is called post-mount.
  const sessionId = useRef(routeSessionId)
  // Mirror messages length, route id, and prior-connected state into refs
  // so the open/close handlers see the latest values without forcing
  // connect() to be recreated. ``connect`` is memoized on credentials only;
  // without the routeSessionId mirror, the replay guard would capture the
  // mount-time route id and miss in-session navigations to a saved chat.
  const messagesLengthRef = useRef(messagesLength)
  messagesLengthRef.current = messagesLength
  const routeSessionIdRef = useRef(routeSessionId)
  routeSessionIdRef.current = routeSessionId
  // An approve/answer prompt is up. The close handler clears it, and only a
  // restore re-raises it, so a drop while it's up needs a replay.
  const promptPendingRef = useRef(false)
  promptPendingRef.current = awaitingApproval || Boolean(pendingInteractive)
  // Set by a drop that the next connect must replay for; cleared once a
  // restore starts (see markNeedsReplay).
  const needsReplayRef = useRef(false)
  // Discriminator for the ERROR handler: a turn-in-flight error (e.g. an
  // LLM/tool failure mid-prompt) belongs inline because the user is
  // staring at the chat waiting for the response. A connection-time
  // error (auth, MCP bootstrap, service unavailable, session-load) is
  // operational and goes through toast — otherwise reconnects pollute
  // the chat with bubbles for events the user didn't trigger.
  const loadingRef = useRef(loading)
  loadingRef.current = loading
  const readOnly = useSelector((state) => state.kodi.readOnly)
  const readOnlyRef = useRef(readOnly)
  readOnlyRef.current = readOnly
  // Events observed while the socket was down or unauthenticated. The BE drops a
  // repeat for a flow this session was already nudged about, so a flush that
  // races another tab is harmless.
  const pendingEventsRef = useRef([])
  const authedRef = useRef(false)
  // Where the transcript ended when a turn this tab didn't start began. The page
  // anchors its loader and current-turn tools to the last user message, which a
  // backend-started turn never adds, so it needs this instead.
  const [hostTurnStartIndex, setHostTurnStartIndex] = useState(null)
  const wasConnectedRef = useRef(false)
  // Tracks the restore envelope synchronously — replay frames arrive in the
  // same tick as session_restore_start, before the redux `restoring` flag
  // could round-trip through a render.
  const inRestoreRef = useRef(false)

  // A stop is pending until the BE's turn_complete (or an error, or the socket
  // dropping) settles the turn. Every BE turn-exit path ends in one of those
  // three, so this is the only teardown a pending stop needs — see stopStreaming.
  const clearStopPending = useCallback(() => {
    setIsStopping(false)
  }, [])

  // Clear messages if org changes mid-session
  useEffect(() => {
    if (prevOrgRef.current && prevOrgRef.current !== organizationid) {
      dispatch(reset())
    }
    prevOrgRef.current = organizationid
  }, [organizationid, dispatch])

  const connect = useCallback(() => {
    if (wsRef.current && wsRef.current.readyState !== WebSocket.CLOSED) {
      return
    }

    setConnectionState(CONNECTION_STATES.CONNECTING)
    setAccessDenied(false)
    setAuthFailed(false)

    const socket = new WebSocket(COWORK_WS_URL)
    wsRef.current = socket
    authedRef.current = false

    // Guard against late events from a previous socket: after resetChat /
    // disconnect closes a socket and creates a new one, the old socket's
    // close/error events can still fire and stomp on the active connection's
    // state. Each handler bails when it isn't the current socket.
    const isStaleSocket = () => socket !== wsRef.current

    // Replay on the next connect if this drop cut off a turn (the BE re-streams
    // it from its start), a restore (the store holds partial history), or an
    // open prompt (only a restore re-raises it). Must run before the handlers
    // clear those signals.
    const markNeedsReplay = () => {
      if (loadingRef.current || inRestoreRef.current || promptPendingRef.current) {
        needsReplayRef.current = true
      }
    }

    // A turn this connection didn't start (a BE-initiated nudge, or another
    // tab's prompt) shows up as a bare text_delta / tool_call_start with no
    // user_message echo. Bring up the same loader state sendMessage sets.
    const markTurnInFlight = () => {
      if (!inRestoreRef.current && !loadingRef.current) {
        setHostTurnStartIndex(messagesLengthRef.current)
        setIsStreaming(true)
        dispatch(setLoading(true))
      }
    }

    socket.addEventListener('open', () => {
      if (isStaleSocket()) {
        return
      }
      const authPayload = {
        type: WS_MESSAGE_TYPES.AUTH,
        token,
        username,
        organizationid: String(organizationid),
      }
      if (sessionId.current) {
        authPayload.session_id = sessionId.current
        if (
          (routeSessionIdRef.current && messagesLengthRef.current === 0) ||
          needsReplayRef.current
        ) {
          authPayload.replay_conversation = true
        }
      }
      socket.send(JSON.stringify(authPayload))
    })

    socket.addEventListener('message', (event) => {
      if (isStaleSocket()) {
        return
      }
      const data = JSON.parse(event.data)

      switch (data.type) {
        case WS_MESSAGE_TYPES.AUTH_SUCCESS:
          setConnectionState(CONNECTION_STATES.CONNECTED)
          sessionId.current = data.session_id
          setServerSessionId(data.session_id)
          wasConnectedRef.current = true
          dispatch(setReadOnly(data.read_only))
          setOwnerOrgId(data.owner_org_id ?? null)
          setIsUnredactedAccess(Boolean(data.is_unredacted_access))
          dispatch(setActiveSessionTitle(data.session_name))
          authedRef.current = true
          if (pendingEventsRef.current.length > 0 && !data.read_only) {
            pendingEventsRef.current.forEach((frame) => socket.send(frame))
          }
          pendingEventsRef.current = []
          break

        case WS_MESSAGE_TYPES.SYSTEM:
          break

        case WS_MESSAGE_TYPES.TURN_STARTED:
          markTurnInFlight()
          break

        case WS_MESSAGE_TYPES.TEXT_DELTA:
          markTurnInFlight()
          streamingTextRef.current += data.text
          setStreamingText(streamingTextRef.current)
          break

        case WS_MESSAGE_TYPES.TOOL_CALL_START:
          markTurnInFlight()
          dispatch(
            addMessage({
              id: data.tool_use_id,
              role: MESSAGE_ROLES.TOOL,
              toolName: data.tool_name,
              internal: data.internal,
              status: TOOL_STATUSES.CALLING,
              input: null,
              result: null,
              isError: false,
            })
          )
          break

        case WS_MESSAGE_TYPES.TOOL_EXECUTING:
          dispatch(
            updateMessage({
              id: data.tool_use_id,
              status: TOOL_STATUSES.EXECUTING,
              input: data.input,
              rationale: data.rationale,
              internal: data.internal,
            })
          )
          break

        case WS_MESSAGE_TYPES.TOOL_PROGRESS:
          dispatch(
            updateMessage({
              id: data.tool_use_id,
              progressMessage: data.message,
              progress: data.progress,
              progressTotal: data.total,
            })
          )
          break

        case WS_MESSAGE_TYPES.TOOL_RESULT:
          dispatch(
            updateMessage({
              id: data.tool_use_id,
              status: TOOL_STATUSES.COMPLETE,
              result: data.result,
              isError: data.is_error,
            })
          )
          break

        case WS_MESSAGE_TYPES.ARTIFACT:
          if (INLINE_ARTIFACT_TYPES.includes(data.artifact_type)) {
            // Chart/table artifacts render inline — don't trigger a download.
            // `plan` (the balto_execute_query args behind a chart) rides along
            // so the chart can be pinned to the dashboard.
            dispatch(
              addMessage({
                role: MESSAGE_ROLES.ARTIFACT,
                artifactType: data.artifact_type,
                data: data.data ?? null,
                plan: data.plan ?? null,
                chartIndex: data.chart_index ?? 0,
                // Whether this chart can be pinned (rolling = pin as-is, blocked
                // = a fixed period/comparison that can't stay current) and the
                // reason when blocked — both computed server-side.
                pinEligibility: data.pin_eligibility ?? null,
                pinReason: data.pin_reason ?? null,
              })
            )
          } else {
            downloadArtifact(data)
            dispatch(
              addMessage({
                role: MESSAGE_ROLES.ARTIFACT,
                artifactType: data.artifact_type,
                filename: data.filename,
              })
            )
          }
          break

        case WS_MESSAGE_TYPES.TURN_COMPLETE: {
          const finalText = streamingTextRef.current
          if (finalText) {
            dispatch(
              addMessage({
                role: MESSAGE_ROLES.KODI,
                content: finalText,
                auditUuid: data.audit_uuid,
                // Set only on replay
                feedbackType: data.feedback_type,
              })
            )
          }
          streamingTextRef.current = ''
          setStreamingText('')
          setIsStreaming(false)
          setHostTurnStartIndex(null)
          clearStopPending()
          dispatch(setLoading(false))
          break
        }

        case WS_MESSAGE_TYPES.ERROR:
          if (loadingRef.current) {
            dispatch(addMessage({ role: MESSAGE_ROLES.ERROR, content: data.error }))
          } else {
            // Shared id collapses identical error toasts across reconnect
            // cycles — when MCP is down, every retry would otherwise stack
            // another "Service temporarily unavailable" toast on top.
            toast.error(data.error, { id: 'kodi-ws-connection-error' })
          }
          setIsStreaming(false)
          clearStopPending()
          dispatch(setLoading(false))
          dispatch(setRestoring(false))
          // A mid-turn error ends the turn, so drop the pending approve/answer
          // prompts too — otherwise they'd linger with no live turn to resolve them.
          dispatch(setAwaitingApproval(false))
          dispatch(setPendingInteractive(null))
          // Clear the restore ref alongside `restoring` — an error mid-restore
          // ends the envelope, and leaving the ref stuck true would make later
          // live user_message echoes read as replays (loader never comes back).
          inRestoreRef.current = false
          streamingTextRef.current = ''
          setStreamingText('')
          break

        case WS_MESSAGE_TYPES.INFO:
          toast.info(data.message)
          break

        case WS_MESSAGE_TYPES.SESSION_RESTORE_START: {
          // Cleared here, not on open: a socket that drops before its restore
          // starts still owes one.
          needsReplayRef.current = false
          inRestoreRef.current = true
          dispatch(reset())
          dispatch(setRestoring(true))
          setIsStreaming(false)
          dispatch(setLoading(false))
          streamingTextRef.current = ''
          setStreamingText('')
          break
        }

        case WS_MESSAGE_TYPES.USER_MESSAGE: {
          dispatch(addMessage({ role: MESSAGE_ROLES.USER, content: data.content }))
          // Outside a restore envelope this is the live-turn echo from the
          // stream tail (reconnect mid-turn, or another tab's prompt). The
          // connection that submitted the prompt never receives it — the BE
          // suppresses the echo there — so seeing one means a turn is in
          // flight that this connection didn't start: bring up the same
          // loader state sendMessage sets for its own turns. That turn may be
          // the answer to an approval/ask_user the restore just re-raised, so
          // clear those the way sendMessage does.
          if (!inRestoreRef.current) {
            setIsStreaming(true)
            dispatch(setLoading(true))
            dispatch(setAwaitingApproval(false))
            dispatch(setPendingInteractive(null))
          }
          break
        }

        case WS_MESSAGE_TYPES.SESSION_RESTORE_END: {
          inRestoreRef.current = false
          dispatch(setRestoring(false))
          break
        }

        case WS_MESSAGE_TYPES.SESSION_RENAMED: {
          dispatch(renameSession({ session_id: data.session_id, name: data.name }))
          // Turn-1 auto-name (or a rename echo) for the open session: keep the
          // header title in sync too, since it reads activeSessionTitle.
          if (data.session_id === sessionId.current) {
            dispatch(setActiveSessionTitle(data.name))
          }
          break
        }

        case WS_MESSAGE_TYPES.APPROVAL_REQUESTED: {
          dispatch(setAwaitingApproval(true))
          break
        }

        case WS_MESSAGE_TYPES.INTERACTIVE_REQUEST: {
          dispatch(
            setPendingInteractive({
              question: data.question,
              options: data.options || [],
              multiSelect: Boolean(data.multi_select),
            })
          )
          break
        }

        case WS_MESSAGE_TYPES.USAGE_UPDATE: {
          // Socket payloads are the trust boundary — default here so the
          // reducer can assign directly.
          dispatch(setUsagePolicies(data.policies || []))
          break
        }

        default:
          break
      }
    })

    // First drop after a healthy session retries within a second so a brief
    // blip doesn't make the user stare at the "Disconnected" pill for 2s.
    // Jittered so a draining pod's clients don't all land in the same tick.
    // Subsequent failures fall into the polling cadence.
    const scheduleReconnect = () => {
      const delay = wasConnectedRef.current
        ? Math.random() * RECONNECT_JITTER_MS
        : RECONNECT_INTERVAL_MS
      wasConnectedRef.current = false
      reconnectTimeoutRef.current = setTimeout(connect, delay)
    }

    socket.addEventListener('close', (event) => {
      if (isStaleSocket()) {
        return
      }
      markNeedsReplay()
      setConnectionState(CONNECTION_STATES.DISCONNECTED)
      setServerSessionId(null)
      inRestoreRef.current = false
      streamingTextRef.current = ''
      setStreamingText('')
      setIsStreaming(false)
      clearStopPending()
      // Drop the loading/restoring flags so an in-flight prompt or restore
      // that died on the socket doesn't leave the UI stuck.
      dispatch(setLoading(false))
      dispatch(setRestoring(false))
      // Same for a pending approve/answer prompt — the socket is gone, so there's
      // no turn left to resolve it.
      dispatch(setAwaitingApproval(false))
      dispatch(setPendingInteractive(null))
      // Terminal close codes from the BE — retrying just stacks identical
      // error toasts forever. 4001 = bad auth, 4003 = org not permitted /
      // session access denied. Both need the user to act (re-login, switch
      // org, refresh) before another connect could possibly succeed.
      if (event.code === 4001 || event.code === 4003) {
        // Neither can recover without user action, and both otherwise fall
        // through to the restore spinner and hang. Surface the in-page
        // "no access" message for both rather than leaving a stuck spinner.
        // Drop the unredacted flags too: a mid-session close (e.g. the
        // support window closes server-side) would otherwise leave the banner
        // gate (isUnredactedAccess && ownerOrgId) true and render it alongside
        // the "no access" message.
        setOwnerOrgId(null)
        setIsUnredactedAccess(false)
        setAccessDenied(true)
        setAuthFailed(event.code === 4001)
        return
      }
      scheduleReconnect()
    })

    // ``close`` is guaranteed to follow ``error`` on a real WS failure;
    // scheduling here too would double-up the reconnect and leak the
    // first timer while clobbering the priming delay on the surviving
    // one. Let the close handler own the reconnect schedule.
    socket.addEventListener('error', () => {
      if (isStaleSocket()) {
        return
      }
      markNeedsReplay()
      setConnectionState(CONNECTION_STATES.DISCONNECTED)
      dispatch(setLoading(false))
      dispatch(setRestoring(false))
    })
  }, [token, username, organizationid, dispatch, clearStopPending])

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = null
    }
    if (wsRef.current) {
      wsRef.current.close()
      wsRef.current = null
    }
    // Nulling wsRef above makes the old socket's close handler bail on the
    // stale-socket guard, so it won't reset the restore ref — clear it here so
    // a session switch mid-restore doesn't carry a stuck-true ref to the next.
    inRestoreRef.current = false
    // A session switch / new chat starts fresh; a replay owed to the old
    // session must not fire on the new one.
    needsReplayRef.current = false
    authedRef.current = false
    // A held event belongs to the session that was open when its backfill
    // finished. Carrying it across a session switch would start a nudge turn in
    // an unrelated chat, so it is dropped with the rest of the connection state.
    pendingEventsRef.current = []
    // Same reason: the close handler won't run, so the pending stop has to be
    // dropped here.
    clearStopPending()
    // Same for the confirmed session id, for the same reason. KodiPage reads one that
    // differs from the route id as "that chat can't be opened", so carrying the
    // outgoing id into a session switch flashes the empty composer mid-restore.
    setServerSessionId(null)
    // Tearing down for a session switch / new chat — drop connection-derived
    // state (read-only lock, pending approval, header title, usage bars) so
    // the next connection starts clean. The routeSessionId effect disconnects + reconnects without a
    // reset() — and URL-driven switches (back/forward, deep links) bypass
    // handleSelectSession — so without clearing the title here the previous
    // session's name lingers in the header until the next auth_success.
    // A transient drop+reconnect goes through the close handler, not here.
    dispatch(setReadOnly(false))
    dispatch(setAwaitingApproval(false))
    dispatch(setPendingInteractive(null))
    dispatch(setActiveSessionTitle(null))
    // The next connection's usage_update re-pushes current standings.
    dispatch(setUsagePolicies([]))
    // Unredacted-view state is per-session too. Clearing it here (not just in
    // resetChat) covers the routeSessionId switch path, which disconnects +
    // reconnects without a reset — otherwise the previous session's banner
    // lingers until the next auth_success. Transient drops reconnect via the
    // close handler, not here, so the banner correctly survives those.
    setOwnerOrgId(null)
    setIsUnredactedAccess(false)
    setAccessDenied(false)
    setAuthFailed(false)
  }, [dispatch])

  // `approval` ('approve' | 'decline'), when set, is the user's explicit decision on a
  // proposed change. It rides along on the prompt so the backend can enforce approval
  // server-side (only an 'approve' authorizes the pending write); a normal message omits
  // it. See KodiApprovalPrompt.
  // Unlike sendMessage: no user bubble and no loader. The BE decides whether the
  // event starts a turn (it may queue or drop it), and the turn's turn_started
  // brings the loader up through markTurnInFlight.
  //
  // A terminal poll can land while the socket is reconnecting or still
  // unauthenticated, and its caller never fires again — so hold the event and
  // flush it once the session is authenticated rather than dropping it.
  const sendEvent = useCallback((payload) => {
    if (readOnlyRef.current) {
      return
    }

    const frame = JSON.stringify({ type: WS_MESSAGE_TYPES.EVENT, ...payload })

    if (!authedRef.current || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      pendingEventsRef.current.push(frame)
      return
    }

    wsRef.current.send(frame)
  }, [])

  // `reportChartId`, when set, is a chart the user opened from Kodi Reports; the backend
  // loads it into this turn so the question is answered against that chart.
  const sendMessage = useCallback(
    (content, { approval, reportChartId } = {}) => {
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        return
      }

      dispatch(addMessage({ role: MESSAGE_ROLES.USER, content }))
      setHostTurnStartIndex(null)
      dispatch(setAwaitingApproval(false))
      dispatch(setPendingInteractive(null))

      streamingTextRef.current = ''
      setStreamingText('')
      setIsStreaming(true)
      dispatch(setLoading(true))

      wsRef.current.send(
        JSON.stringify({
          type: WS_MESSAGE_TYPES.PROMPT,
          content,
          ...(approval ? { approval } : {}),
          ...(reportChartId ? { report_chart_id: reportChartId } : {}),
        })
      )
    },
    [dispatch]
  )

  // Hanging up does NOT stop a turn — the BE keeps it running so a refresh
  // can't kill it, and reconnecting replays it from the start, which is why
  // stopping used to look like it was ignored. Ask the BE to cancel instead and
  // let its turn_complete settle the UI (committing the partial answer through
  // the same path a normal turn takes, on every tab tailing this session).
  //
  // Deliberately no ack deadline. A stop during a tool call isn't seen until that
  // tool returns — the BE skips its per-token check once a tool call is announced —
  // so any timeout short enough to be useful fires on ordinary turns and hangs up,
  // which is the disconnect this replaced. Nothing is needed in its place: every BE
  // turn-exit path ends in turn_complete, an error frame, or a socket close, and all
  // three run clearStopPending. A long "Stopping…" is safe because the composer is
  // already disabled while streaming, so Stop can't leave the user more stuck than
  // not pressing it.
  const stopStreaming = useCallback(() => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      return
    }

    wsRef.current.send(JSON.stringify({ type: WS_MESSAGE_TYPES.STOP }))
    setIsStopping(true)
  }, [])

  const resetChat = useCallback(() => {
    disconnect()
    sessionId.current = null
    dispatch(setLoading(false))
    streamingTextRef.current = ''
    setStreamingText('')
    setIsStreaming(false)
    reconnectTimeoutRef.current = setTimeout(() => {
      connect()
    }, 100)
  }, [connect, disconnect, dispatch])

  useEffect(() => {
    connect()
    return () => {
      disconnect()
    }
  }, [connect, disconnect])

  // Clear the conversation when leaving Kodi (SPA nav to another route). The
  // socket unmounts but the Redux store doesn't, so stale messages linger. On a
  // return to a bare `/kodi` those stale messages make KodiPage's URL-sync
  // effect read `hasUserActivity` as true against the freshly-minted (empty)
  // session, so it history.replaces to that id and prepends a synthetic sidebar
  // row — a "ghost" session with no prompt that never renames and only clears on
  // a full reload. Clearing here means a bare return is genuinely fresh (no
  // ghost), while returning to `/kodi/<id>` replays the real conversation.
  useEffect(() => {
    return () => {
      dispatch(reset())
    }
  }, [dispatch])

  // Reconnect when the user navigates to a different session mid-mount
  // (e.g., /kodi/A → /kodi/B without a refresh). Skipped on initial
  // mount and after our own URL push (where routeSessionId catches up to
  // sessionId.current and the comparison is a no-op).
  useEffect(() => {
    if (routeSessionId && routeSessionId !== sessionId.current) {
      // Clear the outgoing session first, the way selecting one from the sidebar
      // does. Without it the previous chat's messages stay mounted — including a
      // backfill bar that would send its completion event into this session —
      // and the non-empty store also suppresses the replay this switch needs.
      dispatch(reset())
      sessionId.current = routeSessionId
      disconnect()
      connect()
    }
  }, [routeSessionId, connect, disconnect, dispatch])

  return {
    connectionState,
    isStreaming,
    isStopping,
    streamingText,
    serverSessionId,
    ownerOrgId,
    isUnredactedAccess,
    accessDenied,
    authFailed,
    hostTurnStartIndex,
    sendMessage,
    sendEvent,
    stopStreaming,
    resetChat,
  }
}
